/**
 * The booking-money tender state (DNKADM-0402 U2/U5) — the one place the console
 * decides HOW a reservation is paid, shared by the create-on-behalf dialog and the
 * Take payment dialog so the two can never drift.
 *
 * It is deliberately the SAME shape `SellCartDialog` uses for a sale: a card on file
 * (the shared `CardOnFileChooser`) or a card entered at the desk (`CardPaymentField` →
 * a one-shot token), and — for cash / the club's own terminal — an active tenant
 * payment method. The server prices the booking, so there is no amount here: the desk
 * chooses how it is paid, never how much (DNKAPI-0559).
 *
 * **Card credentials are opaque.** `cardId` is a radio value and `cardToken` a
 * single-use nonce; neither is rendered, stored or logged, and exactly one of them ever
 * reaches the wire.
 *
 * The state lives here (a plain module, not a component file) so
 * `ReservationTenderFields.tsx` stays a component-only file — the same Fast Refresh
 * split `reservationPayment.ts` made for `ReservationActionDialogs.tsx`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { listPaymentMethods, type PaymentMethod } from '@/api/paymentMethods';
import { serverMessage } from '@/api/serverMessage';
import type { ReservationPaymentTender } from '@/api/reservations';
import type { CardVerification } from '@/api/payments';
import type { TokenizeFn } from '@/components/payments/CardPaymentField';

/** The two tenders the desk can take for a booking (DNKAPI-0559). */
export type TenderChoice = 'card' | 'external';

/** The payment-method list, loaded only while the external tender is chosen. */
export type TenderMethodsState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; methods: PaymentMethod[] }
  | { status: 'error'; message: string };

export interface ReservationTender {
  /** The chosen card on file, or `null` while a new card is being entered. */
  cardOnFileId: string | null;
  setCardOnFileId: (cardId: string | null) => void;
  /** Register/clear the secure card field's tokenizer (`null` when it isn't ready). */
  setTokenize: (tokenize: TokenizeFn | null) => void;
  /** The chosen tenant payment method (`external`), by id. */
  paymentMethodId: string;
  setPaymentMethodId: (id: string) => void;
  methods: TenderMethodsState;
  /** The chosen method's row, or null (nothing chosen / not loaded). */
  selectedMethod: PaymentMethod | null;
  /** Whether the chosen tender is complete enough to submit. */
  ready: boolean;
  /** Drop every credential — used when the funding choice changes. */
  reset: () => void;
  /**
   * The `payment` object for the request. Tokenizes the entered card when there is no
   * card on file, so it is called ONCE per attempt, at submit time — the nonce is
   * single-use. Throws when the tender is incomplete. `verification` (DNKADM-0487) is what
   * the entered card is for — the booking's charge and the member — so the bank can verify
   * the card first; it is used only on the entered-card path.
   */
  buildPayment: (verification: CardVerification) => Promise<ReservationPaymentTender>;
}

/**
 * Own the tender state for `choice`. `null` means no tender is being taken (credits /
 * pay later), which keeps the method list unfetched and reports `ready`.
 */
export function useReservationTender(choice: TenderChoice | null): ReservationTender {
  const [cardOnFileId, setCardOnFileId] = useState<string | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [methods, setMethods] = useState<TenderMethodsState>({ status: 'idle' });
  const tokenizeRef = useRef<TokenizeFn | null>(null);

  // The method picker is loaded only when the external tender is chosen — exactly as
  // `SellCartDialog` loads it, including the tolerant error copy.
  const external = choice === 'external';
  useEffect(() => {
    if (!external) return;
    let cancelled = false;
    setMethods({ status: 'loading' });
    void listPaymentMethods('active')
      .then((rows) => {
        if (!cancelled) setMethods({ status: 'ready', methods: rows });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setMethods({
          status: 'error',
          message: serverMessage(error) ?? 'Payment methods could not be loaded.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [external]);

  const setTokenize = useCallback((tokenize: TokenizeFn | null) => {
    tokenizeRef.current = tokenize;
    setCardReady(tokenize !== null);
  }, []);

  const reset = useCallback(() => {
    tokenizeRef.current = null;
    setCardReady(false);
    setCardOnFileId(null);
    setPaymentMethodId('');
  }, []);

  const selectedMethod =
    methods.status === 'ready'
      ? (methods.methods.find((method) => method.id === paymentMethodId) ?? null)
      : null;

  const ready =
    choice === null
      ? true
      : choice === 'card'
        ? cardOnFileId !== null || cardReady
        : selectedMethod !== null;

  const buildPayment = useCallback(
    async (verification: CardVerification): Promise<ReservationPaymentTender> => {
      if (choice === 'external') {
        const id = paymentMethodId.trim();
        if (id.length === 0) throw new Error('Choose how this payment was taken.');
        return { paymentMethodId: id };
      }
      // A card on file needs no tokenizer — there is nothing left to enter.
      if (cardOnFileId !== null) return { cardId: cardOnFileId };
      const tokenize = tokenizeRef.current;
      if (tokenize === null) throw new Error('Finish entering the card before charging.');
      return { cardToken: await tokenize(verification) };
    },
    [cardOnFileId, choice, paymentMethodId],
  );

  return {
    cardOnFileId,
    setCardOnFileId,
    setTokenize,
    paymentMethodId,
    setPaymentMethodId,
    methods,
    selectedMethod,
    ready,
    reset,
    buildPayment,
  };
}
